import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Typography } from '@mui/material';
import DeployDialog from '../components/deployments/DeployDialog';
import RecentDeployments from '../components/deployments/RecentDeployments';
import { useGetDevicesQuery } from '@store/api/deviceApi';
import type { DeploymentKind } from '@store/api/deploymentsApi';
import type { Device } from '../types/api';

interface MaintenanceAction {
  kind: DeploymentKind;
  version: string;
  title: string;
  notice: React.ReactNode;
}

const ACTIONS = {
  runtime: {
    kind: 'dotnet_runtime',
    version: '10.0',
    title: 'Move devices to .NET 10.0 runtime',
    notice: (
      <Alert severity="warning">
        Each device installs dotnet-runtime-10.0 from Microsoft&apos;s apt repository and restarts
        the .NET application on it. If the application does not stay up for 45 seconds, its start
        script is rolled back to the old runtime. On success /opt/tools/dotnet* is deleted.
      </Alert>
    ),
  },
  check: {
    kind: 'os_packages',
    version: 'check',
    title: 'Check OS updates',
    notice: (
      <Alert severity="info">
        Each device refreshes its package lists and reports which packages can be upgraded.
        Nothing is installed.
      </Alert>
    ),
  },
  upgrade: {
    kind: 'os_packages',
    version: 'upgrade',
    title: 'Upgrade OS packages',
    notice: (
      <Alert severity="warning">
        Each device runs apt-get upgrade (no dist-upgrade, config files and holds are kept). If a
        dotnet package was upgraded the .NET application is restarted. Try 1-2 devices first. The
        device is not rebooted; use the reboot command afterwards if a reboot is required.
      </Alert>
    ),
  },
} satisfies Record<string, MaintenanceAction>;

type ActionName = keyof typeof ACTIONS;

/** "10.0.12" -> "10.0.x"; devices without a reported runtime are "unknown". */
const runtimeCounts = (devices: Device[]): [string, number][] => {
  const counts = new Map<string, number>();
  for (const d of devices) {
    const key = d.dotnetRuntimeVersion
      ? `${d.dotnetRuntimeVersion.split('.').slice(0, 2).join('.')}.x`
      : 'unknown';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts].sort(([a], [b]) =>
    a === 'unknown' ? 1 : b === 'unknown' ? -1 : b.localeCompare(a, undefined, { numeric: true })
  );
};

/** Fleet-wide .NET runtime move and OS package upgrades. */
const MaintenancePage: React.FC = () => {
  const [action, setAction] = useState<ActionName | null>(null);
  const { data: devicesData } = useGetDevicesQuery({ pageSize: 999 });

  // Deployments only reach verified devices, so only they are counted.
  const verified = useMemo(
    () => (devicesData?.data ?? []).filter((d) => d.registrationStatus === 'verified'),
    [devicesData]
  );
  const runtimes = useMemo(() => runtimeCounts(verified), [verified]);
  const pending = verified.filter((d) => (d.osUpdatesPending ?? 0) > 0).length;
  const rebootRequired = verified.filter((d) => d.rebootRequired).length;
  const neverChecked = verified.filter((d) => !d.osUpdatesCheckedAt).length;

  const current = action && ACTIONS[action];

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{
        mb: 3
      }}>
        Maintenance
      </Typography>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          .NET runtime
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginBottom: '16px'
          }}>
          A one-time move of each device onto the .NET runtime from Microsoft&apos;s apt
          repository. Later runtime patches arrive with OS package upgrades. The device must be
          able to reach packages.microsoft.com.
        </Typography>
        {devicesData && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {runtimes.map(([runtime, count]) => (
              <Chip key={runtime} label={`${runtime}: ${count}`} />
            ))}
          </Box>
        )}
        <Button variant="contained" onClick={() => setAction('runtime')}>
          Move devices to .NET 10…
        </Button>
      </Paper>
      <RecentDeployments kind="dotnet_runtime" title="Recent .NET runtime deployments" />

      <Paper sx={{ p: 2, mt: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          OS packages
        </Typography>
        {devicesData && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Updates pending: ${pending}`} color={pending ? 'warning' : 'default'} />
            <Chip
              label={`Reboot required: ${rebootRequired}`}
              color={rebootRequired ? 'warning' : 'default'}
            />
            <Chip label={`Never checked: ${neverChecked}`} />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setAction('check')}>
            Check for updates…
          </Button>
          <Button variant="contained" onClick={() => setAction('upgrade')}>
            Upgrade packages…
          </Button>
        </Box>
      </Paper>
      <RecentDeployments kind="os_packages" title="Recent OS package deployments" />

      {current && (
        <DeployDialog
          kind={current.kind}
          version={current.version}
          title={current.title}
          notice={current.notice}
          onClose={() => setAction(null)}
        />
      )}
    </Box>
  );
};

export default MaintenancePage;
